import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

// ============================================
// UI STATE INTERFACE
// ============================================

interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  
  // Theme
  theme: 'light' | 'dark' | 'system';
  
  // Language
  language: 'fr' | 'ar' | 'en';
  
  // Modals
  activeModal: string | null;
  modalData: any;
  
  // Breadcrumbs
  breadcrumbs: Array<{ label: string; href?: string }>;
  
  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapsed: () => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setLanguage: (language: 'fr' | 'ar' | 'en') => void;
  openModal: (modalId: string, data?: any) => void;
  closeModal: () => void;
  setBreadcrumbs: (breadcrumbs: Array<{ label: string; href?: string }>) => void;
}

// ============================================
// UI STORE
// ============================================

export const useUIStore = create<UIState>()(
  persist(
    immer((set) => ({
      // Initial state
      sidebarOpen: true,
      sidebarCollapsed: false,
      theme: 'light',
      language: 'fr',
      activeModal: null,
      modalData: null,
      breadcrumbs: [],
      
      // Toggle sidebar
      toggleSidebar: () => {
        set((state) => {
          state.sidebarOpen = !state.sidebarOpen;
        });
      },
      
      // Set sidebar open state
      setSidebarOpen: (open: boolean) => {
        set((state) => {
          state.sidebarOpen = open;
        });
      },
      
      // Toggle sidebar collapsed
      toggleSidebarCollapsed: () => {
        set((state) => {
          state.sidebarCollapsed = !state.sidebarCollapsed;
        });
      },
      
      // Set theme
      setTheme: (theme: 'light' | 'dark' | 'system') => {
        set((state) => {
          state.theme = theme;
        });
        
        // Update document class
        const root = document.documentElement;
        root.classList.remove('light', 'dark');
        
        if (theme === 'system') {
          const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : 'light';
          root.classList.add(systemTheme);
        } else {
          root.classList.add(theme);
        }
      },
      
      // Set language
      setLanguage: (language: 'fr' | 'ar' | 'en') => {
        set((state) => {
          state.language = language;
        });
        
        // Update document direction
        const dir = language === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.setAttribute('dir', dir);
        document.documentElement.setAttribute('lang', language);
        
        // Update localStorage for i18n
        localStorage.setItem('i18nextLng', language);
      },
      
      // Open modal
      openModal: (modalId: string, data?: any) => {
        set((state) => {
          state.activeModal = modalId;
          state.modalData = data;
        });
      },
      
      // Close modal
      closeModal: () => {
        set((state) => {
          state.activeModal = null;
          state.modalData = null;
        });
      },
      
      // Set breadcrumbs
      setBreadcrumbs: (breadcrumbs: Array<{ label: string; href?: string }>) => {
        set((state) => {
          state.breadcrumbs = breadcrumbs;
        });
      },
    })),
    {
      name: 'ui-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        language: state.language,
      }),
    }
  )
);

// ============================================
// SELECTORS
// ============================================

export const selectSidebarOpen = (state: UIState) => state.sidebarOpen;
export const selectSidebarCollapsed = (state: UIState) => state.sidebarCollapsed;
export const selectTheme = (state: UIState) => state.theme;
export const selectLanguage = (state: UIState) => state.language;
export const selectActiveModal = (state: UIState) => state.activeModal;
export const selectModalData = (state: UIState) => state.modalData;
export const selectBreadcrumbs = (state: UIState) => state.breadcrumbs;
